const env = (import.meta as any).env ?? {};

export const keycloakAuthUrl = env.VITE_KEYCLOAK_AUTH_URL ?? "";
export const keycloakTokenUrl = env.VITE_KEYCLOAK_TOKEN_URL ?? "";
export const keycloakClientId = env.VITE_KEYCLOAK_CLIENT_ID ?? "";
export const keycloakRedirectUri = env.VITE_KEYCLOAK_REDIRECT_URI ?? "";
export const keycloakScope = env.VITE_KEYCLOAK_SCOPE ?? "openid";
export const keycloakLoginUrl = env.VITE_KEYCLOAK_LOGIN_URL ?? "";
export const keycloakResetUrl = env.VITE_KEYCLOAK_RESET_URL ?? "";
export const keycloakChangePasswordUrl = env.VITE_KEYCLOAK_CHANGE_PASSWORD_URL ?? "";
const rawPkceMethod = env.VITE_KEYCLOAK_PKCE_METHOD ?? "S256";
export const keycloakPkceMethod = String(rawPkceMethod).toUpperCase();

export const getKeycloakPasswordUrl = () =>
  keycloakChangePasswordUrl || keycloakResetUrl || "";

export const getKeycloakIssuer = () => {
  if (!keycloakAuthUrl) return "";
  return keycloakAuthUrl.replace(/\/protocol\/openid-connect\/auth.*$/, "");
};

const KEYCLOAK_VERIFIER_KEY = "kc_pkce_verifier";
const KEYCLOAK_STATE_KEY = "kc_pkce_state";

export function storePkce(verifier: string, state: string) {
  try {
    sessionStorage.setItem(KEYCLOAK_VERIFIER_KEY, verifier);
    sessionStorage.setItem(KEYCLOAK_STATE_KEY, state);
  } catch {
    // ignore session storage failures
  }
  try {
    localStorage.setItem(KEYCLOAK_VERIFIER_KEY, verifier);
    localStorage.setItem(KEYCLOAK_STATE_KEY, state);
  } catch {
    // ignore local storage failures
  }
}

export function consumePkce(expectedState?: string) {
  const read = (storage: Storage) => ({
    verifier: storage.getItem(KEYCLOAK_VERIFIER_KEY) || "",
    state: storage.getItem(KEYCLOAK_STATE_KEY) || "",
  });

  const fromSession = read(sessionStorage);
  const fromLocal = read(localStorage);

  let selected = fromSession;
  if (expectedState) {
    if (fromSession.state && fromSession.state !== expectedState && fromLocal.state === expectedState) {
      selected = fromLocal;
    } else if (!fromSession.state && fromLocal.state === expectedState) {
      selected = fromLocal;
    }
  } else if (!fromSession.verifier && fromLocal.verifier) {
    selected = fromLocal;
  }

  try {
    sessionStorage.removeItem(KEYCLOAK_VERIFIER_KEY);
    sessionStorage.removeItem(KEYCLOAK_STATE_KEY);
  } catch {
    // ignore
  }
  try {
    localStorage.removeItem(KEYCLOAK_VERIFIER_KEY);
    localStorage.removeItem(KEYCLOAK_STATE_KEY);
  } catch {
    // ignore
  }
  return selected;
}

function base64UrlEncode(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export function generateVerifier(length = 64) {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes.buffer);
}

export async function generateChallenge(verifier: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(digest);
}

export function buildAuthUrl(params: Record<string, string>) {
  const url = new URL(keycloakAuthUrl);
  Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  return url.toString();
}

export function getTokenUrl() {
  if (keycloakTokenUrl) return keycloakTokenUrl;
  if (!keycloakAuthUrl) return "";
  return keycloakAuthUrl.replace(/\/auth(\?.*)?$/, "/token");
}

export function getLogoutUrl() {
  if (!keycloakAuthUrl) return "";
  return keycloakAuthUrl.replace(/\/auth(\?.*)?$/, "/logout");
}
